<template>
  <v-card flat>
    <v-card-title class="primaryDialogTitle">
      <span class="headline">{{ isNew ? 'Add New Doctor' : 'Details of Doctor' }}</span>
      <v-spacer></v-spacer>
      <v-btn icon @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text>
      <ValidationObserver ref="form" v-slot="{ invalid }">
        <!-- Status Toggles Row -->
        <v-row class="mb-4">
          <v-col cols="12" md="6">
            <div class="d-flex align-center">
              <span class="mr-4">Active</span>
              <v-switch inset v-model="formData.enabled" color="primary" hide-details />
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="d-flex align-center">
              <span class="mr-4">Show in the app</span>
              <v-switch
                inset
                v-model="formData.isAvailableForBooking"
                color="primary"
                hide-details
              />
            </div>
          </v-col>
        </v-row>

        <!-- Created Date and Status -->
        <v-row>
          <v-col cols="12" md="6">
            <v-menu
              v-if="isNew"
              v-model="dateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <FletrixTextField
                  v-model="formData.createdDate"
                  label="Created Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker v-model="formData.createdDate" @input="dateMenu = false" />
            </v-menu>
            <FletrixTextField
              v-else
              :value="formatDate(get(doctorData, 'created_at'))"
              label="Created Date"
              prepend-icon="mdi-calendar"
              readonly
            />
          </v-col>

          <v-col cols="12" md="6">
            <!-- <v-select
              v-model="formData.status"
              :items="statusOptions"
              label="Status"
              item-text="text"
              item-value="value"
              
            /> -->
          </v-col>
        </v-row>

        <!-- English Names -->
        <v-row>
          <v-col cols="12" md="6">
            <ValidationProvider v-slot="{ errors }" name="Last Name (EN)" rules="required">
              <FletrixTextField
                v-model="formData.lastName.en"
                label="Last Name (EN)"
                :error="!!errors.length"
                :error-messages="errors"
              />
            </ValidationProvider>
          </v-col>

          <v-col cols="12" md="6">
            <ValidationProvider v-slot="{ errors }" name="First Name (EN)" rules="required">
              <FletrixTextField
                v-model="formData.firstName.en"
                label="First Name (EN)"
                :error="!!errors.length"
                :error-messages="errors"
              />
            </ValidationProvider>
          </v-col>
        </v-row>

        <!-- Chinese Traditional Names -->
        <v-row>
          <v-col cols="12" md="6">
            <ValidationProvider v-slot="{ errors }" name="Last Name (ZH-HK)" rules="required">
              <FletrixTextField
                v-model="formData.lastName['zh-hk']"
                label="Last Name (ZH-HK)"
                :error="!!errors.length"
                :error-messages="errors"
              />
            </ValidationProvider>
          </v-col>

          <v-col cols="12" md="6">
            <ValidationProvider v-slot="{ errors }" name="First Name (ZH-HK)" rules="required">
              <FletrixTextField
                v-model="formData.firstName['zh-hk']"
                label="First Name (ZH-HK)"
                :error="!!errors.length"
                :error-messages="errors"
              />
            </ValidationProvider>
          </v-col>
        </v-row>

        <!-- Chinese Simplified Names -->
        <v-row>
          <v-col cols="12" md="6">
            <ValidationProvider v-slot="{ errors }" name="Last Name (ZH-CN)" rules="required">
              <FletrixTextField
                v-model="formData.lastName['zh-cn']"
                label="Last Name (ZH-CN)"
                :error="!!errors.length"
                :error-messages="errors"
              />
            </ValidationProvider>
          </v-col>

          <v-col cols="12" md="6">
            <ValidationProvider v-slot="{ errors }" name="First Name (ZH-CN)" rules="required">
              <FletrixTextField
                v-model="formData.firstName['zh-cn']"
                label="First Name (ZH-CN)"
                :error="!!errors.length"
                :error-messages="errors"
              />
            </ValidationProvider>
          </v-col>
        </v-row>

        <!-- Contact Number -->
        <v-row>
          <v-col cols="12" md="6">
            <FletrixPhone ref="phoneValidator" label="Contact No." v-model="formData.phone" />
          </v-col>
        </v-row>

        <!-- Description English -->
        <v-row>
          <v-col cols="12">
            <FletrixTextarea
              v-model="formData.description.en"
              label="Description (EN)"
              rows="3"
              placeholder="Director, General Practitioner"
            />
          </v-col>
        </v-row>

        <!-- Description Chinese Traditional -->
        <v-row>
          <v-col cols="12">
            <FletrixTextarea
              v-model="formData.description['zh-hk']"
              label="Description (ZH-HK)"
              rows="3"
              placeholder="簡介"
            />
          </v-col>
        </v-row>

        <!-- Description Chinese Simplified -->
        <v-row>
          <v-col cols="12">
            <FletrixTextarea
              v-model="formData.description['zh-cn']"
              label="Description (ZH-CN)"
              rows="3"
              placeholder="简介"
            />
          </v-col>
        </v-row>

        <!-- Avatar Section -->
        <v-row>
          <v-col cols="12">
            <div class="subtitle-1 mb-3">Avatar</div>
            <v-card outlined>
              <v-row>
                <v-col cols="12" md="6">
                  <div class="d-flex flex-column align-center pa-4">
                    <v-avatar size="120" class="mb-3">
                      <v-img v-if="avatarPreviewUrl" :src="avatarPreviewUrl" alt="Doctor Avatar" />
                      <v-img v-else-if="avatarUrl" :src="avatarUrl" alt="Doctor Avatar" />
                      <v-icon v-else size="60" color="grey">mdi-account</v-icon>
                    </v-avatar>
                    <v-btn
                      v-if="avatarUrl || avatarPreviewUrl"
                      small
                      color="error"
                      @click="removeAvatar"
                    >
                      <v-icon small left>mdi-delete</v-icon>
                      Remove
                    </v-btn>
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="pa-4">
                    <v-file-input
                      v-model="newAvatar"
                      label="Select Avatar"
                      outlined
                      flat
                      accept="image/jpeg,image/png,image/jpg"
                      prepend-icon="mdi-camera"
                      show-size
                      @change="onAvatarSelected"
                    />
                    <div v-if="newAvatar" class="text-caption text--secondary mt-2">
                      Avatar will be uploaded when you save
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
        <v-divider class="my-4" />
        <!-- Certificates Section -->
        <v-row>
          <v-col cols="12">
            <div class="d-flex align-center mb-2">
              <custom-label item-field-name="Certificates" />
              <v-spacer></v-spacer>
            </div>
            <div class="d-flex align-center flex-gap">
              <v-btn class="icon-button primary" @click="openCertificateDialog">
                <v-icon size="32">mdi-plus</v-icon>
              </v-btn>
              <template v-if="formData.certificates.length > 0">
                <v-chip
                  v-for="(cert, index) in formData.certificates"
                  :key="index"
                  class="ma-1"
                  close
                  @click:close="removeCertificate(index)"
                >
                  {{ cert }}
                </v-chip>
              </template>
              <div v-else class="text--secondary">No certificates added</div>
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-4" />

        <!-- On Duty Days Section -->
        <v-row>
          <v-col cols="12">
            <div class="d-flex align-center justify-space-between mb-3">
              <custom-label item-field-name="On Duty Days" />
            </div>
            <WeekSchedule
              :open-hours="formData.onDutyDays"
              status-label="On Duty"
              active-label="On Duty"
              inactive-label="Off"
              inactive-chip-label="Off"
              @update="updateOnDutyDays"
            />
            <v-btn color="secondary" type="primary" @click="openHolidaysDialog">
              Manage Holidays
            </v-btn>
          </v-col>
        </v-row>
      </ValidationObserver>
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn text @click="$emit('close')">Cancel</v-btn>
      <v-btn color="success" :loading="loading" :disabled="invalid" @click="save">
        {{ isNew ? 'Create' : 'Save' }}
      </v-btn>
    </v-card-actions>

    <!-- Certificate Dialog -->
    <v-dialog v-model="certificateDialog" max-width="400">
      <v-card>
        <v-card-title class="primaryDialogTitle">Add Certificate</v-card-title>
        <v-card-text>
          <FletrixTextField
            v-model="newCertificate"
            label="Certificate"
            outlined
            flat
            placeholder="e.g., MBChB(CUHK)"
            @keyup.enter="addCertificate"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="certificateDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="addCertificate">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Doctor Holidays Dialog -->
    <v-dialog v-model="holidaysDialog" max-width="900px" scrollable>
      <v-card>
        <v-card-title class="primaryDialogTitle">
          <span class="headline">Doctor Holidays - {{ getDoctorFullName() }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeHolidaysDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <ClinicHolidays
            v-if="holidaysDialog && (doctorId || formData._id)"
            :doctor-id="doctorId || formData._id"
            :doctor-name="getDoctorFullName()"
            :clinic-id="clinicId"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { RESTFUL } from '@/data/constants';
import api from '@/api';
import { mapActions } from 'vuex';
import moment from 'moment';
import { get, template } from 'lodash';
import WeekSchedule from './WeekSchedule';
import ClinicHolidays from './ClinicHolidays';
import { uploadSingleAttachment, deleteAttachment } from '@/api/attachment';
import { attnThumbHref } from '@/services/filters';
import { FletrixTextField, FletrixPhone, FletrixTextarea } from '@/components/Atom';
import customLabel from '@/components/customFormLabel/customLabel';
import { crudRead } from '@/api/crud';

export default {
  name: 'DoctorCard',
  components: {
    WeekSchedule,
    ClinicHolidays,
    FletrixTextField,
    FletrixPhone,
    FletrixTextarea,
    customLabel,
  },
  props: {
    doctorId: {
      type: String,
      default: null,
    },
    clinicId: {
      type: String,
      required: true,
    },
    doctorData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      dateMenu: false,
      certificateDialog: false,
      newCertificate: '',
      newAvatar: null,
      avatarPreviewUrl: null,
      holidaysDialog: false,
      formData: {
        enabled: true,
        isAvailableForBooking: true,
        createdDate: new Date().toISOString().substr(0, 10),
        status: '1$accepted',
        lastName: {
          en: '',
          'zh-hk': '',
          'zh-cn': '',
        },
        firstName: {
          en: '',
          'zh-hk': '',
          'zh-cn': '',
        },
        phone: '',
        description: {
          en: '',
          'zh-hk': '',
          'zh-cn': '',
        },
        certificates: [],
        onDutyDays: [],
        avatar: null,
      },
      statusOptions: [
        { text: 'Active', value: '1$accepted' },
        { text: 'Pending', value: '0$requested' },
        { text: 'Inactive', value: '2$rejected' },
      ],
      invalid: false,
    };
  },
  computed: {
    isNew() {
      return !this.doctorId || this.doctorId === 'new';
    },
    avatarUrl() {
      return this.formData.avatar ? attnThumbHref(this.formData.avatar, 200) : null;
    },
  },
  watch: {
    doctorData: {
      immediate: true,
      handler(newVal) {
        if (newVal && !this.isNew) {
          this.parseDoctorData(newVal);
        }
      },
    },
  },
  async mounted() {
    if (this.doctorData && !this.isNew) {
      this.parseDoctorData(this.doctorData);
    } else if (this.isNew) {
      // For new doctors, fetch clinic open hours and set as default onDutyDays
      await this.fetchClinicOpenHours();
    }
  },
  methods: {
    ...mapActions('alert', ['updateAlertMessage']),
    get,

    formatDate(date) {
      return date ? moment(date).format('YYYY-MM-DD') : '';
    },

    async fetchClinicOpenHours() {
      try {
        const clinicData = await crudRead(api, RESTFUL.clinicProfile.read, this.clinicId, {
          select: ['openHours', 'breakHours'],
        });

        // // Fetch clinic data to get open hours
        // const response = await api.get(`${RESTFUL.clinicProfile.read}/${this.clinicId}`, {
        //   select: ['openHours', 'breakHours'],
        // });
        // const clinicData = response.data;

        // If clinic has open hours, set them as default onDutyDays
        if (clinicData && clinicData.openHours && Array.isArray(clinicData.openHours)) {
          this.formData.onDutyDays = [...clinicData.openHours];
        }
      } catch (error) {
        console.error('Error fetching clinic open hours:', error);
        // Don't show error message to user as this is just for default values
        // If it fails, the doctor will just have empty onDutyDays initially
      }
    },

    parseDoctorData(data) {
      if (!data) return;

      // Basic fields
      this.formData.enabled = data.enabled !== false;
      this.formData.isAvailableForBooking = data.isAvailableForBooking !== false;
      this.formData.status = data.status || '1$accepted';

      // Parse lastName
      if (data.lastName) {
        try {
          const lastName =
            typeof data.lastName === 'string' ? JSON.parse(data.lastName) : data.lastName;
          this.formData.lastName = { ...this.formData.lastName, ...lastName };
        } catch (e) {
          console.error('Error parsing last name:', e);
        }
      }

      // Parse firstName
      if (data.firstName) {
        try {
          const firstName =
            typeof data.firstName === 'string' ? JSON.parse(data.firstName) : data.firstName;
          this.formData.firstName = { ...this.formData.firstName, ...firstName };
        } catch (e) {
          console.error('Error parsing first name:', e);
        }
      }

      // Parse description
      if (data.description) {
        try {
          const description =
            typeof data.description === 'string' ? JSON.parse(data.description) : data.description;
          this.formData.description = { ...this.formData.description, ...description };
        } catch (e) {
          console.error('Error parsing description:', e);
        }
      }

      // Parse phone - PhoneValidator expects full phone with country code
      if (data.phone) {
        this.formData.phone = data.phone;
      }

      // Parse certificates
      if (data.certificates && Array.isArray(data.certificates)) {
        this.formData.certificates = [...data.certificates];
      }

      // Parse onDutyDays
      if (data.onDutyDays && Array.isArray(data.onDutyDays)) {
        this.formData.onDutyDays = [...data.onDutyDays];
      }

      // Parse avatar
      if (data.avatar) {
        this.formData.avatar = data.avatar;
      }
    },

    openCertificateDialog() {
      this.newCertificate = '';
      this.certificateDialog = true;
    },

    addCertificate() {
      if (this.newCertificate.trim()) {
        this.formData.certificates.push(this.newCertificate.trim());
        this.newCertificate = '';
        this.certificateDialog = false;
      }
    },

    removeCertificate(index) {
      this.formData.certificates.splice(index, 1);
    },

    updateOnDutyDays(onDutyDays) {
      this.formData.onDutyDays = onDutyDays;
    },

    getDoctorFullName() {
      const firstName = this.formData.firstName?.en || '';
      const lastName = this.formData.lastName?.en || '';
      return `Dr. ${firstName} ${lastName}`.trim();
    },

    openHolidaysDialog() {
      if (this.doctorId || this.formData._id) {
        this.holidaysDialog = true;
      } else {
        this.updateAlertMessage({
          msg: 'Please save the doctor first before managing holidays',
          type: 'warning',
        });
      }
    },

    closeHolidaysDialog() {
      this.holidaysDialog = false;
    },

    onAvatarSelected(file) {
      if (file) {
        // Create preview URL for the selected file
        this.avatarPreviewUrl = URL.createObjectURL(file);
      } else {
        // Clear preview if no file selected
        if (this.avatarPreviewUrl) {
          URL.revokeObjectURL(this.avatarPreviewUrl);
          this.avatarPreviewUrl = null;
        }
      }
    },

    async removeAvatar() {
      // Clear the new avatar file if selected
      this.newAvatar = null;

      // Clear preview URL
      if (this.avatarPreviewUrl) {
        URL.revokeObjectURL(this.avatarPreviewUrl);
        this.avatarPreviewUrl = null;
      }

      // If there's an existing avatar, mark it for deletion
      if (this.formData.avatar) {
        // We'll handle actual deletion during save
        this.formData.avatar = null;
      }
    },

    async save() {
      const isValid =
        this.$refs.form && this.$refs.form.validate ? await this.$refs.form.validate() : false;
      if (!isValid) {
        return;
      }

      try {
        this.loading = true;

        // Upload new avatar if selected
        let avatarId = this.formData.avatar;
        if (this.newAvatar) {
          try {
            const attachment = await uploadSingleAttachment(api, this.newAvatar);
            avatarId = attachment._id;
          } catch (error) {
            console.error('Error uploading avatar:', error);
            await this.updateAlertMessage({
              msg: 'Failed to upload avatar',
              type: 'error',
            });
            this.loading = false;
            return;
          }
        }

        const payload = {
          lastName: JSON.stringify(this.formData.lastName),
          firstName: JSON.stringify(this.formData.firstName),
          description: JSON.stringify(this.formData.description),
          clinic: this.clinicId,
          enabled: this.formData.enabled,
          isAvailableForBooking: this.formData.isAvailableForBooking,
          status: this.formData.status,
          certificates: this.formData.certificates,
          onDutyDays: this.formData.onDutyDays,
          avatar: avatarId,
        };

        // Add phone if provided - PhoneValidator already includes country code
        if (this.formData.phone) {
          payload.phone = this.formData.phone;
        }

        let response;
        if (this.isNew) {
          response = await api.post('rml/admin/DoctorProfiles', payload);
          await this.updateAlertMessage({
            msg: 'Doctor created successfully',
            type: 'success',
            color: 'success',
          });
        } else {
          response = await api.patch(`rml/admin/DoctorProfiles/${this.doctorId}`, payload);
          await this.updateAlertMessage({
            msg: 'Doctor updated successfully',
            type: 'success',
            color: 'success',
          });
        }

        // Clean up preview URL
        if (this.avatarPreviewUrl) {
          URL.revokeObjectURL(this.avatarPreviewUrl);
          this.avatarPreviewUrl = null;
        }

        this.$emit('saved', response.data);
        this.$emit('close');
      } catch (error) {
        console.error('Error saving doctor:', error);
        await this.updateAlertMessage({
          msg: `Failed to ${this.isNew ? 'create' : 'update'} doctor`,
          type: 'error',
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.v-card {
  max-width: 900px;
  margin: 0 auto;
}
</style>
